import React, { useState, forwardRef } from 'react';
import { debounce } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Button } from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.ts';
import { Loader } from '../../Elements/Loader/index.tsx';
import { Tag } from '../../Elements/Tag/index.tsx';
import { useNotification, Notification } from '../../Feedback/Notifications/index.tsx';
import { CopyTooltip } from '../../Feedback/Tooltip/CopyTooltip/index.tsx';
import { Tooltip } from '../../Feedback/Tooltip/index.tsx';
import { Input } from '../Input/index.tsx';
import Label from '../Label/index.tsx';
import { CreatableListItem } from './components/index.ts';
import { PLACEHOLDERS } from './constants.ts';
import {
  Wrapper,
  InputWrapper,
  ListWrapper,
  ListHeader,
  List,
  ListItem,
  ListFooter,
  Total,
  Suggestions,
  SortOrderWrapper,
} from './styled.ts';
import type { CreatableListProps, Option } from './types.ts';
import { SortOrder } from './types.ts';

const replaceSpaces = (str: string) => str.replace(/\s+/g, '');

export const CreatableList = forwardRef<HTMLInputElement, CreatableListProps>(function CreatableList(
  {
    value = [],
    onChange,
    validateNewItem,
    formatOptionLabel,
    disabled = false,
    error = false,
    placeholders = PLACEHOLDERS,
    dataTestId,
    actions,
    suggestions,
    isLoading,
    allowSpacing = true,
  },
  ref,
) {
  const { colors } = useTheme();
  const notify = useNotification();
  const [inputValue, setInputValue] = useState<string>('');
  const [pastedInfo, setPastedInfo] = useState<{ all: number; pasted: number }>();
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const isEditMode = !!onChange;
  const [t] = useTranslation(['backend', 'common']);

  const validateNewValue = useCallbackRef((newValue: string) => {
    if (newValue.trim().length === 0) {
      return placeholders.errorValueEmpty;
    }
    if (value.some((option) => option.label.toLowerCase() === newValue.toLowerCase())) {
      return placeholders.errorValueExists;
    }
    if (validateNewItem) {
      return validateNewItem(newValue);
    }

    return undefined;
  });

  const isValidItem = useCallbackRef((inputValue: string) => {
    const error = validateNewValue(inputValue);

    return !error;
  });

  const validationError = React.useMemo(
    () => (inputValue.length > 0 ? validateNewValue(inputValue) : undefined),
    [inputValue, validateNewValue],
  );

  const isValidNewValue = React.useMemo(() => (isEditMode ? isValidItem(inputValue) : false), [inputValue, isEditMode, isValidItem]);

  const handleSortOrderChange = useCallbackRef(() =>
    setSortOrder((current) => (current === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC)),
  );
  const filteredAndSortedValues = React.useMemo(
    () =>
      value
        .filter((option) => {
          if (inputValue.length > 0) {
            const formattedLabel = formatOptionLabel?.(option);
            const label = typeof formattedLabel === 'string' ? formattedLabel : option.label;
            return label.toLowerCase().includes(inputValue.toLowerCase());
          }
          return true;
        })
        .sort((a, b) => {
          if (!formatOptionLabel) {
            return sortOrder === SortOrder.ASC ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
          }

          const aLabel = formatOptionLabel(a)?.toString() ?? '';
          const bLabel = formatOptionLabel(b)?.toString() ?? '';

          return sortOrder === SortOrder.ASC ? aLabel.localeCompare(bLabel) : bLabel.localeCompare(aLabel);
        }),
    [formatOptionLabel, inputValue, sortOrder, value],
  );
  const validatedSuggestions = React.useMemo(
    () => suggestions?.options.filter((option) => isValidItem(option.label)),
    [isValidItem, suggestions?.options],
  );

  const handleCopyValues = useCallbackRef(async () => navigator.clipboard.writeText(value.map((item) => item.label).join(',')));
  const handlePasteValues = useCallbackRef(async () => {
    let pastedText;
    try {
      pastedText = await navigator.clipboard.readText();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        notify.error({ title: placeholders.errorTitle, body: placeholders.errorAccessDenied });
      } else {
        notify.error({ title: placeholders.errorTitle, body: placeholders.errorAccessError });
      }
      return;
    }

    const splittedItems = pastedText
      .split(',')
      .map((s) => {
        const trimmedString = s.trim();
        return allowSpacing ? trimmedString : replaceSpaces(trimmedString);
      })
      .filter(Boolean);
    const uniqueItems = Array.from(new Set(splittedItems));
    const newItems = uniqueItems.filter((item) => isValidItem(item)).map((item) => ({ label: item, value: item }));

    if (splittedItems.length > 0) {
      setPastedInfo({ all: splittedItems.length, pasted: newItems.length });
    }

    if (newItems.length > 0) {
      onChange?.([...value, ...newItems]);
    }
  });

  const handleRemoveAll = useCallbackRef(() => onChange?.([]));
  const handleRemoveItem = useCallbackRef((itemToRemove: string) => {
    const newValue = value.filter((item) => item.value !== itemToRemove);
    onChange?.(newValue);
  });
  const handleCreateItem = useCallbackRef((item?: Option) => {
    onChange?.([...value, item ?? { label: inputValue, value: inputValue }]);
    setInputValue('');
  });

  const handleClearInput = useDebouncedCallback(() => setInputValue(''), 100);
  const handleInputChange = useCallbackRef((e: React.ChangeEvent<HTMLInputElement>) =>
    setInputValue(allowSpacing ? e.target.value : replaceSpaces(e.target.value)),
  );

  const handleInputKeyDown = useCallbackRef((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (!isEditMode) {
        return;
      }

      e.preventDefault();

      if (validationError) {
        notify.error({
          title: placeholders.errorTitle ?? t('common:ERROR'),
          body: validationError,
        });
        return;
      }

      handleCreateItem();
    }
  });

  const clearInfoTimeout = React.useMemo(() => debounce(() => setPastedInfo(undefined), 3000), []);
  const onMainTooltipVisibilityChange = useCallbackRef((visible: boolean) => visible && setPastedInfo(undefined));
  React.useEffect(() => pastedInfo && clearInfoTimeout(), [clearInfoTimeout, pastedInfo]);

  return (
    <Wrapper data-testid={dataTestId}>
      <InputWrapper>
        <Input
          ref={ref}
          data-testid={dataTestId && `${dataTestId}-input`}
          placeholder={placeholders.input}
          icon={
            inputValue ? (
              <Icons.Clear filled size={20} color={colors.grey100_1} onClick={handleClearInput} />
            ) : (
              <Icons.Search size={20} color={colors.grey100_1} />
            )
          }
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleClearInput}
          isActionIcon={!!inputValue}
          value={inputValue}
          disabled={disabled}
        />
        {isEditMode && (
          <>
            <CopyTooltip
              onClick={handleCopyValues}
              tooltip={placeholders.copy ?? ''}
              data-testid={dataTestId && `${dataTestId}-copy-tooltip`}
              copiedTooltip={placeholders.copied ?? ''}>
              <Button data-testid={dataTestId && `${dataTestId}-copy-button`} iconOnly secondary disabled={disabled || value.length === 0}>
                <Icons.Copy />
              </Button>
            </CopyTooltip>
            <Tooltip
              data-testid={dataTestId && `${dataTestId}-paste-tooltip`}
              disabled={disabled}
              placement='top'
              tooltip={placeholders.paste}
              trigger='hover'
              onVisibleChange={onMainTooltipVisibilityChange}>
              <Tooltip
                data-testid={dataTestId && `${dataTestId}-paste-info-tooltip`}
                placement='top'
                tooltip={`${placeholders.pasted} ${pastedInfo?.pasted} ${placeholders.totalSeparator} ${pastedInfo?.all}`}
                delayShow={0}
                visible={!!pastedInfo}>
                <Button
                  data-testid={dataTestId && `${dataTestId}-paste-button`}
                  iconOnly
                  secondary
                  onClick={handlePasteValues}
                  disabled={disabled}>
                  <Icons.Past />
                </Button>
              </Tooltip>
            </Tooltip>
          </>
        )}
        {actions}
      </InputWrapper>
      {isEditMode && suggestions && validatedSuggestions && (
        <>
          {validatedSuggestions.length > 0 && (
            <div>
              <Label data-testid={dataTestId && `${dataTestId}-suggestions-label`}>{suggestions.label}</Label>
              <Suggestions data-testid={dataTestId && `${dataTestId}-suggestions`}>
                {suggestions.pending && <Loader data-testid={dataTestId && `${dataTestId}-suggestions-loader`} cover />}
                {validatedSuggestions.map((item) => (
                  <Tag
                    isDisabled={disabled}
                    onClick={() => handleCreateItem(item)}
                    key={item.value}
                    title={item.label}
                    data-testid={dataTestId && `${dataTestId}-suggestion-${item.value}`}
                  />
                ))}
              </Suggestions>
            </div>
          )}
          {validatedSuggestions.length === 0 && !suggestions.pending && (
            <Notification data-testid={dataTestId && `${dataTestId}-suggestions-notification`} body={suggestions.notification} fullWidth />
          )}
        </>
      )}
      <ListWrapper $error={error} data-testid={dataTestId && `${dataTestId}-list-wrapper`}>
        <ListHeader>
          <span data-testid={dataTestId && `${dataTestId}-selected-label`}>{placeholders.selected}</span>
          {isEditMode && (
            <Tooltip
              tooltip={placeholders.clearAll}
              disabled={disabled || value.length === 0}
              data-testid={dataTestId && `${dataTestId}-clear-all-tooltip`}
              placement='top'>
              <Button
                ghost
                onClick={handleRemoveAll}
                data-testid={dataTestId && `${dataTestId}-clear-all-button`}
                disabled={disabled || value.length === 0}>
                <Icons.Clear />
              </Button>
            </Tooltip>
          )}
        </ListHeader>
        <List data-testid={dataTestId && `${dataTestId}-list`} $selectedLength={value.length} $minHeight={isValidNewValue || !!isLoading}>
          {isLoading && (
            <ListItem data-testid={dataTestId && `${dataTestId}-loading-item`}>
              <Loader data-testid={dataTestId && `${dataTestId}-loader`} height={20} fullWidth />
            </ListItem>
          )}
          {isValidNewValue && (
            <ListItem data-testid={dataTestId && `${dataTestId}-create-item`} $isPlaceholder onClick={() => handleCreateItem()}>
              <span>
                {placeholders.create}
                {` "${inputValue}"`}
              </span>
            </ListItem>
          )}
          {filteredAndSortedValues.map((item) => (
            <CreatableListItem
              key={item.value}
              item={item}
              formatOptionLabel={formatOptionLabel}
              disabled={disabled}
              isEditMode={isEditMode}
              dataTestId={dataTestId}
              onRemove={handleRemoveItem}
            />
          ))}
        </List>
        <ListFooter>
          <SortOrderWrapper data-testid={dataTestId && `${dataTestId}-sort-order`} onClick={handleSortOrderChange}>
            {sortOrder === SortOrder.ASC ? placeholders.sortOrderAsc : placeholders.sortOrderDesc}
          </SortOrderWrapper>
          <Total data-testid={dataTestId && `${dataTestId}-total`}>
            {filteredAndSortedValues.length === value.length
              ? `${value.length} ${placeholders.total}`
              : `${filteredAndSortedValues.length} ${placeholders.totalSeparator} ${value.length}`}
          </Total>
        </ListFooter>
      </ListWrapper>
    </Wrapper>
  );
});
